import {
  Background,
  BackgroundVariant,
  Controls,
  Handle,
  MarkerType,
  MiniMap,
  Position,
  ReactFlow,
  ReactFlowProvider,
  applyEdgeChanges,
  applyNodeChanges,
  type Connection,
  type Edge,
  type EdgeChange,
  type Node,
  type NodeChange,
  type NodeProps,
} from "@xyflow/react";
import {
  Activity,
  ArrowLeft,
  Bell,
  Check,
  ChevronRight,
  CircleAlert,
  Clock3,
  Copy,
  GitBranch,
  MoreHorizontal,
  Pause,
  Play,
  Plus,
  RefreshCw,
  Save,
  Search,
  Send,
  Sparkles,
  Trash2,
  Workflow,
  Zap,
  type LucideIcon,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type DragEvent,
} from "react";

import {
  connectAutomationsApp,
  resolveAutomationsView,
  type AutomationsAppSession,
} from "./app-session";
import {
  ACTION_LABELS,
  AUTOMATIONS_V1_ACTION_KINDS,
  AUTOMATIONS_V1_EVENT_TYPES,
  EVENT_LABELS,
  WORKFLOW_TEMPLATES,
  createId,
  createNode,
  createWorkflow,
  publishWorkflow,
  validateWorkflow,
  WorkflowValidationError,
  type ActionKind,
  type WorkflowDefinition,
  type WorkflowEdge,
  type WorkflowNode,
  type WorkflowNodeKind,
  type WorkflowStatus,
  type AutomationNotification,
} from "./workflow-model";
import {
  runWorkflowPreview,
  sampleEvent,
  type WorkflowExecution,
} from "./workflow-runtime";
import { RuntimeWorkflowStore } from "./workflow-store";

type FlowNode = Node<{ workflowNode: WorkflowNode }, "workflowStep">;
type FlowEdge = Edge<{ branch?: WorkflowEdge["branch"] }>;

const NODE_LIBRARY: Array<{
  kind: WorkflowNodeKind;
  title: string;
  description: string;
  icon: LucideIcon;
  group: "Start" | "Logic" | "Actions";
}> = [
  {
    kind: "TRIGGER",
    title: "CrewFSM event",
    description: "Start when business data changes",
    icon: Zap,
    group: "Start",
  },
  {
    kind: "CONDITION",
    title: "Condition",
    description: "Continue down a true or false path",
    icon: GitBranch,
    group: "Logic",
  },
  {
    kind: "DELAY",
    title: "Delay",
    description: "Wait before the next step",
    icon: Clock3,
    group: "Logic",
  },
  {
    kind: "ACTION",
    title: "Action",
    description: "Create an office notification",
    icon: Send,
    group: "Actions",
  },
];

const NODE_ICONS: Record<WorkflowNodeKind, LucideIcon> = {
  TRIGGER: Zap,
  CONDITION: GitBranch,
  DELAY: Clock3,
  ACTION: Send,
};

export function AutomationsApp() {
  const [session, setSession] = useState<AutomationsAppSession | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    let connected: AutomationsAppSession | null = null;
    void connectAutomationsApp()
      .then((value) => {
        connected = value;
        if (!active) {
          value.bridge.destroy();
          return;
        }
        applyTheme(value.context.themeContract);
        setSession(value);
      })
      .catch((error: unknown) => {
        if (active) setFailure(errorMessage(error));
      });
    return () => {
      active = false;
      connected?.bridge.destroy();
    };
  }, []);

  if (failure) return <ConnectionError message={failure} />;
  if (!session) return <AppLoading />;
  return <AutomationsWorkspace session={session} />;
}

function AutomationsWorkspace({ session }: { session: AutomationsAppSession }) {
  const store = useMemo(() => new RuntimeWorkflowStore(session), [session]);
  const [revision, setRevision] = useState(0);
  const [loading, setLoading] = useState(true);
  const [runtimeFailure, setRuntimeFailure] = useState<string | null>(null);
  const view = resolveAutomationsView(session.context.currentRoute);
  const workflows = useMemo(() => store.list(), [store, revision]);
  const executions = useMemo(() => store.listExecutions(), [store, revision]);
  const notifications = useMemo(
    () => store.listNotifications(),
    [store, revision],
  );
  const refresh = useCallback(() => setRevision((value) => value + 1), []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    void store
      .hydrate()
      .then(() => {
        if (!active) return;
        refresh();
        setRuntimeFailure(null);
      })
      .catch((error: unknown) => {
        if (active) setRuntimeFailure(errorMessage(error));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [refresh, store]);

  useEffect(() => {
    if (loading || runtimeFailure || view.name === "BUILDER") return;
    let active = true;
    const poll = async () => {
      if (document.visibilityState !== "visible") return;
      try {
        await store.hydrate();
        if (active) refresh();
      } catch {
        // Keep the last good snapshot. The initial loader remains responsible
        // for presenting a blocking runtime connection error.
      }
    };
    const interval = window.setInterval(() => void poll(), 10_000);
    return () => {
      active = false;
      window.clearInterval(interval);
    };
  }, [loading, refresh, runtimeFailure, store, view.name]);

  useEffect(() => {
    const title =
      view.name === "BUILDER" ? "Workflow builder" : titleCase(view.name);
    void session.bridge.setTitle(`Automations · ${title}`);
  }, [session.bridge, view]);

  const openWorkflow = useCallback(
    (workflowId: string) => {
      void session.bridge.navigate(`/workflows/${workflowId}`);
    },
    [session.bridge],
  );

  const createFromTemplate = useCallback(
    async (templateId?: string) => {
      const template = WORKFLOW_TEMPLATES.find(
        (item) => item.id === templateId,
      );
      const workflow = createWorkflow("Untitled workflow", template);
      try {
        await store.save(workflow);
        refresh();
        openWorkflow(workflow.id);
      } catch (error) {
        void session.bridge.showToast(errorMessage(error), "CRITICAL");
      }
    },
    [openWorkflow, refresh, session.bridge, store],
  );

  if (loading) return <AppLoading />;
  if (runtimeFailure) {
    return (
      <ConnectionError
        message={`Automations runtime is unavailable. ${runtimeFailure}`}
      />
    );
  }

  if (view.name === "BUILDER") {
    const workflow = store.find(view.workflowId);
    if (!workflow) {
      return (
        <MissingWorkflow
          onBack={() => void session.bridge.navigate("/workflows")}
        />
      );
    }
    return (
      <ReactFlowProvider>
        <WorkflowBuilder
          initialWorkflow={workflow}
          onBack={() => void session.bridge.navigate("/workflows")}
          onExecution={async (execution) => {
            await store.appendExecution(execution);
            refresh();
          }}
          onSave={async (next) => {
            await store.save(next);
            refresh();
          }}
          session={session}
        />
      </ReactFlowProvider>
    );
  }

  if (view.name === "WORKFLOWS") {
    return (
      <WorkflowList
        onCreate={() => void createFromTemplate()}
        onDuplicate={async (workflow) => {
          try {
            const duplicate = duplicateWorkflow(workflow);
            await store.save(duplicate);
            refresh();
            openWorkflow(duplicate.id);
          } catch (error) {
            void session.bridge.showToast(errorMessage(error), "CRITICAL");
          }
        }}
        onOpen={openWorkflow}
        onRemove={async (workflow) => {
          if (
            !window.confirm(`Delete “${workflow.name}”? This cannot be undone.`)
          ) {
            return;
          }
          try {
            await store.remove(workflow.id);
            refresh();
            void session.bridge.showToast("Workflow deleted.", "SUCCESS");
          } catch (error) {
            void session.bridge.showToast(errorMessage(error), "CRITICAL");
          }
        }}
        onStatusChange={async (workflow, status) => {
          try {
            await store.save({
              ...workflow,
              status,
              updatedAt: new Date().toISOString(),
            });
            refresh();
            void session.bridge.showToast(
              status === "ACTIVE" ? "Workflow activated." : "Workflow paused.",
              "SUCCESS",
            );
          } catch (error) {
            void session.bridge.showToast(errorMessage(error), "CRITICAL");
          }
        }}
        workflows={workflows}
      />
    );
  }

  if (view.name === "ACTIVITY") {
    return (
      <ActivityView
        executions={executions}
        onRetry={async (executionId) => {
          await store.retryExecution(executionId);
          refresh();
          void session.bridge.showToast(
            "Execution queued for retry.",
            "SUCCESS",
          );
        }}
        workflows={workflows}
      />
    );
  }

  return (
    <Overview
      executions={executions}
      notifications={notifications}
      onCreate={(templateId) => void createFromTemplate(templateId)}
      onOpenActivity={() => void session.bridge.navigate("/activity")}
      onOpenWorkflow={openWorkflow}
      onOpenWorkflows={() => void session.bridge.navigate("/workflows")}
      workflows={workflows}
    />
  );
}

function Overview({
  workflows,
  executions,
  notifications,
  onCreate,
  onOpenWorkflow,
  onOpenWorkflows,
  onOpenActivity,
}: {
  workflows: WorkflowDefinition[];
  executions: WorkflowExecution[];
  notifications: AutomationNotification[];
  onCreate: (templateId?: string) => void;
  onOpenWorkflow: (workflowId: string) => void;
  onOpenWorkflows: () => void;
  onOpenActivity: () => void;
}) {
  const activeCount = workflows.filter(
    (item) => item.status === "ACTIVE",
  ).length;
  const successCount = executions.filter(
    (item) => item.status === "SUCCEEDED",
  ).length;
  return (
    <main className="app-page overview-page">
      <PageHeader
        action={
          <button
            className="button button-primary"
            onClick={() => onCreate()}
            type="button"
          >
            <Plus size={16} /> New workflow
          </button>
        }
        description="Connect CrewFSM events to clear, repeatable actions."
        title="Overview"
      />

      <section aria-label="Automation summary" className="metrics-grid">
        <Metric label="Workflows" value={String(workflows.length)} />
        <Metric label="Active" value={String(activeCount)} />
        <Metric label="Successful runs" value={String(successCount)} />
      </section>

      <section className="content-section">
        <SectionHeading
          action={
            <TextButton onClick={onOpenWorkflows}>View workflows</TextButton>
          }
          description="Start with a proven service-business pattern, then make it yours."
          title="Workflow templates"
        />
        <div className="template-grid">
          {WORKFLOW_TEMPLATES.filter((template) =>
            AUTOMATIONS_V1_EVENT_TYPES.includes(
              template.eventType as (typeof AUTOMATIONS_V1_EVENT_TYPES)[number],
            ),
          ).map((template) => (
            <button
              className="template-card"
              key={template.id}
              onClick={() => onCreate(template.id)}
              type="button"
            >
              <span className="template-icon">
                <Workflow size={19} />
              </span>
              <span className="template-copy">
                <strong>{template.name}</strong>
                <span>{template.description}</span>
              </span>
              <ChevronRight aria-hidden="true" size={18} />
            </button>
          ))}
        </div>
      </section>

      <section className="content-section split-section">
        <div>
          <SectionHeading
            action={<TextButton onClick={onOpenWorkflows}>Manage</TextButton>}
            title="Recently edited"
          />
          <div className="compact-list">
            {workflows.slice(0, 4).map((workflow) => (
              <button
                className="compact-row"
                key={workflow.id}
                onClick={() => onOpenWorkflow(workflow.id)}
                type="button"
              >
                <StatusDot status={workflow.status} />
                <span>
                  <strong>{workflow.name}</strong>
                  <small>{triggerLabel(workflow)}</small>
                </span>
                <span className="row-meta">
                  {formatRelative(workflow.updatedAt)}
                </span>
              </button>
            ))}
            {!workflows.length && <InlineEmpty label="No workflows yet." />}
          </div>
        </div>
        <div>
          <SectionHeading
            action={
              <TextButton onClick={onOpenActivity}>View activity</TextButton>
            }
            title="Latest runs"
          />
          <div className="compact-list">
            {executions.slice(0, 4).map((execution) => (
              <div
                className="compact-row compact-row-static"
                key={execution.id}
              >
                <RunStatus status={execution.status} />
                <span>
                  <strong>{execution.workflowName}</strong>
                  <small>
                    {execution.test ? "Test" : "Live"} ·{" "}
                    {EVENT_LABELS[execution.triggerEventType]}
                  </small>
                </span>
                <span className="row-meta">
                  {formatRelative(execution.startedAt)}
                </span>
              </div>
            ))}
            {!executions.length && (
              <InlineEmpty label="Workflow runs will appear here." />
            )}
          </div>
        </div>
      </section>

      {notifications.length ? (
        <section className="content-section">
          <SectionHeading title="Automation notifications" />
          <div className="compact-list">
            {notifications.slice(0, 5).map((notification) => (
              <div
                className="compact-row compact-row-static"
                key={notification.id}
              >
                <span className="notification-icon">
                  <Bell size={15} />
                </span>
                <span>
                  <strong>{notification.title}</strong>
                  <small>{notification.message}</small>
                </span>
                <span className="row-meta">
                  {formatRelative(notification.createdAt)}
                </span>
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </main>
  );
}

function WorkflowList({
  workflows,
  onCreate,
  onOpen,
  onDuplicate,
  onRemove,
  onStatusChange,
}: {
  workflows: WorkflowDefinition[];
  onCreate: () => void;
  onOpen: (id: string) => void;
  onDuplicate: (workflow: WorkflowDefinition) => void;
  onRemove: (workflow: WorkflowDefinition) => void;
  onStatusChange: (
    workflow: WorkflowDefinition,
    status: WorkflowStatus,
  ) => void;
}) {
  const [query, setQuery] = useState("");
  const filtered = workflows.filter((workflow) =>
    `${workflow.name} ${workflow.description}`
      .toLowerCase()
      .includes(query.toLowerCase()),
  );
  return (
    <main className="app-page">
      <PageHeader
        action={
          <button
            className="button button-primary"
            onClick={onCreate}
            type="button"
          >
            <Plus size={16} /> New workflow
          </button>
        }
        description="Draft, publish, pause, and inspect each automation."
        title="Workflows"
      />
      <div className="toolbar">
        <label className="search-control">
          <Search aria-hidden="true" size={16} />
          <span className="sr-only">Search workflows</span>
          <input
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search workflows"
            value={query}
          />
        </label>
        <span className="toolbar-count">
          {filtered.length} workflow{filtered.length === 1 ? "" : "s"}
        </span>
      </div>
      {filtered.length ? (
        <section className="workflow-table" aria-label="Workflows">
          <div className="workflow-table-head">
            <span>Name</span>
            <span>Trigger</span>
            <span>Status</span>
            <span>Updated</span>
            <span />
          </div>
          {filtered.map((workflow) => (
            <div className="workflow-table-row" key={workflow.id}>
              <button
                className="workflow-name"
                onClick={() => onOpen(workflow.id)}
                type="button"
              >
                <span className="workflow-icon">
                  <Workflow size={17} />
                </span>
                <span>
                  <strong>{workflow.name}</strong>
                  <small>{workflow.description || "No description"}</small>
                </span>
              </button>
              <span>{triggerLabel(workflow)}</span>
              <StatusBadge status={workflow.status} />
              <span>{formatRelative(workflow.updatedAt)}</span>
              <details className="row-menu">
                <summary aria-label={`Actions for ${workflow.name}`}>
                  <MoreHorizontal size={18} />
                </summary>
                <div className="menu-content">
                  <button onClick={() => onOpen(workflow.id)} type="button">
                    <Workflow size={15} /> Edit
                  </button>
                  <button onClick={() => onDuplicate(workflow)} type="button">
                    <Copy size={15} /> Duplicate
                  </button>
                  {workflow.status === "ACTIVE" ? (
                    <button
                      onClick={() => onStatusChange(workflow, "PAUSED")}
                      type="button"
                    >
                      <Pause size={15} /> Pause
                    </button>
                  ) : workflow.version > 0 ? (
                    <button
                      onClick={() => onStatusChange(workflow, "ACTIVE")}
                      type="button"
                    >
                      <Play size={15} /> Activate
                    </button>
                  ) : null}
                  <button
                    className="danger-menu-item"
                    onClick={() => onRemove(workflow)}
                    type="button"
                  >
                    <Trash2 size={15} /> Delete
                  </button>
                </div>
              </details>
            </div>
          ))}
        </section>
      ) : (
        <EmptyState
          actionLabel="Create workflow"
          description={
            query
              ? "Try a different search."
              : "Build your first event-driven workflow."
          }
          icon={Workflow}
          onAction={query ? () => setQuery("") : onCreate}
          title={query ? "No matching workflows" : "Automations start here"}
        />
      )}
    </main>
  );
}

function ActivityView({
  executions,
  workflows,
  onRetry,
}: {
  executions: WorkflowExecution[];
  workflows: WorkflowDefinition[];
  onRetry: (executionId: string) => Promise<void>;
}) {
  const [status, setStatus] = useState<"ALL" | WorkflowExecution["status"]>(
    "ALL",
  );
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [retryFailure, setRetryFailure] = useState<string | null>(null);
  const filtered = executions.filter(
    (execution) => status === "ALL" || execution.status === status,
  );
  return (
    <main className="app-page activity-page">
      <PageHeader
        description="Live and test execution history with step-level audit details."
        title="Activity"
      />
      <div className="toolbar activity-toolbar">
        <label className="select-control">
          <span className="sr-only">Filter by status</span>
          <select
            onChange={(event) => setStatus(event.target.value as typeof status)}
            value={status}
          >
            <option value="ALL">All statuses</option>
            <option value="SUCCEEDED">Succeeded</option>
            <option value="FAILED">Failed</option>
            <option value="RUNNING">Running</option>
            <option value="WAITING">Waiting</option>
            <option value="DEAD_LETTER">Dead letter</option>
          </select>
        </label>
        <span className="toolbar-count">
          {workflows.length} workflows · {filtered.length} runs
        </span>
      </div>
      {retryFailure ? (
        <p className="activity-error" role="alert">
          {retryFailure}
        </p>
      ) : null}
      {filtered.length ? (
        <section className="activity-list" aria-label="Workflow activity">
          {filtered.map((execution) => (
            <details className="activity-row" key={execution.id}>
              <summary>
                <RunStatus status={execution.status} />
                <span>
                  <strong>{execution.workflowName}</strong>
                  <small>{EVENT_LABELS[execution.triggerEventType]}</small>
                </span>
                <span className="activity-version">
                  {execution.test ? "Test" : "Live"} · v
                  {execution.workflowVersion}
                </span>
                <span className="row-meta">
                  {formatDateTime(execution.startedAt)}
                </span>
                <ChevronRight className="activity-chevron" size={18} />
              </summary>
              <div className="step-log">
                {execution.steps.map((step, index) => (
                  <div className="step-log-row" key={`${step.nodeId}-${index}`}>
                    <span
                      className={`step-marker step-${step.status.toLowerCase()}`}
                    >
                      {step.status === "SUCCEEDED" ? (
                        <Check size={12} />
                      ) : (
                        <CircleAlert size={12} />
                      )}
                    </span>
                    <span>
                      <strong>{step.label}</strong>
                      <small>{step.detail}</small>
                    </span>
                    <time>{formatTime(step.completedAt)}</time>
                  </div>
                ))}
                {execution.status === "DEAD_LETTER" ? (
                  <button
                    className="button button-secondary activity-retry"
                    disabled={retryingId === execution.id}
                    onClick={(event) => {
                      event.preventDefault();
                      setRetryingId(execution.id);
                      setRetryFailure(null);
                      void onRetry(execution.id)
                        .catch((error: unknown) =>
                          setRetryFailure(errorMessage(error)),
                        )
                        .finally(() => setRetryingId(null));
                    }}
                    type="button"
                  >
                    <RefreshCw
                      className={
                        retryingId === execution.id ? "spin" : undefined
                      }
                      size={15}
                    />{" "}
                    {retryingId === execution.id
                      ? "Retrying…"
                      : "Retry failed step"}
                  </button>
                ) : null}
              </div>
            </details>
          ))}
        </section>
      ) : (
        <EmptyState
          description="Test a workflow or wait for a subscribed CrewFSM event."
          icon={Activity}
          title="No execution history"
        />
      )}
    </main>
  );
}

function WorkflowBuilder({
  initialWorkflow,
  session,
  onBack,
  onSave,
  onExecution,
}: {
  initialWorkflow: WorkflowDefinition;
  session: AutomationsAppSession;
  onBack: () => void;
  onSave: (workflow: WorkflowDefinition) => Promise<void>;
  onExecution: (execution: WorkflowExecution) => Promise<void>;
}) {
  const [draft, setDraft] = useState(() => clone(initialWorkflow));
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [search, setSearch] = useState("");
  const [running, setRunning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [issuesVisible, setIssuesVisible] = useState(false);
  const issues = useMemo(() => validateWorkflow(draft), [draft]);
  const selectedNode =
    draft.nodes.find((node) => node.id === selectedNodeId) ?? null;

  const updateDraft = useCallback(
    (updater: (current: WorkflowDefinition) => WorkflowDefinition) => {
      setDraft((current) => updater(current));
      setDirty(true);
    },
    [],
  );

  const save = useCallback(async () => {
    const next = { ...draft, updatedAt: new Date().toISOString() };
    setSaving(true);
    try {
      await onSave(next);
      setDraft(next);
      setDirty(false);
      void session.bridge.showToast("Workflow draft saved.", "SUCCESS");
    } catch (error) {
      void session.bridge.showToast(errorMessage(error), "CRITICAL");
    } finally {
      setSaving(false);
    }
  }, [draft, onSave, session.bridge]);

  const runTest = useCallback(async () => {
    const trigger = draft.nodes.find((node) => node.config.kind === "TRIGGER");
    if (!trigger || trigger.config.kind !== "TRIGGER") {
      setIssuesVisible(true);
      void session.bridge.showToast(
        "Add one valid trigger before testing.",
        "CRITICAL",
      );
      return;
    }
    setRunning(true);
    try {
      const execution = await runWorkflowPreview(
        draft,
        sampleEvent(trigger.config.eventType),
      );
      await onExecution(execution);
      void session.bridge.showToast(
        "Test run completed successfully.",
        "SUCCESS",
      );
    } catch (error) {
      setIssuesVisible(true);
      void session.bridge.showToast(errorMessage(error), "CRITICAL");
    } finally {
      setRunning(false);
    }
  }, [draft, onExecution, session.bridge]);

  const publish = useCallback(async () => {
    try {
      const next = publishWorkflow(draft);
      setSaving(true);
      await onSave(next);
      setDraft(next);
      setDirty(false);
      setIssuesVisible(false);
      void session.bridge.showToast(
        `Workflow version ${next.version} published.`,
        "SUCCESS",
      );
    } catch (error) {
      if (error instanceof WorkflowValidationError) setIssuesVisible(true);
      void session.bridge.showToast(errorMessage(error), "CRITICAL");
    } finally {
      setSaving(false);
    }
  }, [draft, onSave, session.bridge]);

  const flowNodes = useMemo(
    () => toFlowNodes(draft.nodes, selectedNodeId),
    [draft.nodes, selectedNodeId],
  );
  const flowEdges = useMemo(() => toFlowEdges(draft.edges), [draft.edges]);

  const onNodesChange = useCallback(
    (changes: NodeChange<FlowNode>[]) => {
      const persistentChanges = changes.filter(
        (change) => change.type === "position" || change.type === "remove",
      );
      if (persistentChanges.length === 0) return;
      const nextNodes = applyNodeChanges(
        persistentChanges,
        toFlowNodes(draft.nodes, selectedNodeId),
      );
      updateDraft((current) => {
        const nodeIds = new Set(nextNodes.map((node) => node.id));
        return {
          ...current,
          nodes: nextNodes.map((node) => ({
            ...node.data.workflowNode,
            position: node.position,
          })),
          edges: current.edges.filter(
            (edge) => nodeIds.has(edge.source) && nodeIds.has(edge.target),
          ),
        };
      });
      if (
        selectedNodeId &&
        changes.some(
          (change) => change.type === "remove" && change.id === selectedNodeId,
        )
      ) {
        setSelectedNodeId(null);
      }
    },
    [draft.nodes, selectedNodeId, updateDraft],
  );

  const onEdgesChange = useCallback(
    (changes: EdgeChange<FlowEdge>[]) => {
      const nextEdges = applyEdgeChanges(changes, toFlowEdges(draft.edges));
      updateDraft((current) => ({
        ...current,
        edges: fromFlowEdges(nextEdges),
      }));
    },
    [draft.edges, updateDraft],
  );

  const onConnect = useCallback(
    (connection: Connection) => {
      if (
        !connection.source ||
        !connection.target ||
        connection.source === connection.target
      )
        return;
      const source = draft.nodes.find((node) => node.id === connection.source);
      const branch =
        source?.config.kind === "CONDITION"
          ? connection.sourceHandle === "false"
            ? "FALSE"
            : "TRUE"
          : undefined;
      const exists = draft.edges.some(
        (edge) =>
          edge.source === connection.source &&
          edge.target === connection.target &&
          edge.branch === branch,
      );
      if (exists) return;
      updateDraft((current) => ({
        ...current,
        edges: [
          ...current.edges,
          {
            id: createId("edge"),
            source: connection.source!,
            target: connection.target!,
            branch,
          },
        ],
      }));
    },
    [draft.edges, draft.nodes, updateDraft],
  );

  const addNode = useCallback(
    (kind: WorkflowNodeKind, position?: { x: number; y: number }) => {
      if (
        kind === "TRIGGER" &&
        draft.nodes.some((node) => node.config.kind === "TRIGGER")
      ) {
        void session.bridge.showToast(
          "A workflow can only have one trigger.",
          "WARNING",
        );
        return;
      }
      const next = createNode(
        kind,
        position ?? {
          x: 220 + draft.nodes.length * 38,
          y: 100 + draft.nodes.length * 76,
        },
      );
      updateDraft((current) => ({
        ...current,
        nodes: [...current.nodes, next],
      }));
      setSelectedNodeId(next.id);
    },
    [draft.nodes, session.bridge, updateDraft],
  );

  const updateNode = useCallback(
    (nextNode: WorkflowNode) => {
      updateDraft((current) => ({
        ...current,
        nodes: current.nodes.map((node) =>
          node.id === nextNode.id ? nextNode : node,
        ),
      }));
    },
    [updateDraft],
  );

  return (
    <main className="builder-shell">
      <header className="builder-header">
        <button
          aria-label="Back to workflows"
          className="icon-button"
          onClick={onBack}
          type="button"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="builder-title">
          <input
            aria-label="Workflow name"
            onChange={(event) =>
              updateDraft((current) => ({
                ...current,
                name: event.target.value,
              }))
            }
            value={draft.name}
          />
          <StatusBadge status={draft.status} />
        </div>
        <span className="save-state">
          {dirty ? "Unsaved changes" : "Saved"}
        </span>
        <div className="builder-actions">
          <button
            className="button button-secondary"
            disabled={!dirty || saving}
            onClick={() => void save()}
            type="button"
          >
            <Save size={16} /> Save
          </button>
          <button
            className="button button-secondary"
            disabled={running}
            onClick={() => void runTest()}
            type="button"
          >
            {running ? (
              <RefreshCw className="spin" size={16} />
            ) : (
              <Play size={16} />
            )}{" "}
            Test
          </button>
          <button
            className="button button-primary"
            disabled={saving}
            onClick={() => void publish()}
            type="button"
          >
            <Sparkles size={16} /> Publish
          </button>
        </div>
      </header>

      {issuesVisible && issues.length > 0 && (
        <div className="validation-banner" role="alert">
          <CircleAlert size={17} />
          <span>
            <strong>
              {issues.length} item{issues.length === 1 ? "" : "s"} need
              attention
            </strong>
            <small>{issues[0]!.message}</small>
          </span>
          <button onClick={() => setIssuesVisible(false)} type="button">
            Dismiss
          </button>
        </div>
      )}

      <div className={`builder-grid${selectedNode ? " has-inspector" : ""}`}>
        <NodeLibrary
          hasTrigger={draft.nodes.some(
            (node) => node.config.kind === "TRIGGER",
          )}
          onAdd={addNode}
          search={search}
          setSearch={setSearch}
        />
        <WorkflowCanvas
          edges={flowEdges}
          nodes={flowNodes}
          onAdd={addNode}
          onConnect={onConnect}
          onEdgesChange={onEdgesChange}
          onNodesChange={onNodesChange}
          onSelectNode={setSelectedNodeId}
        />
        {selectedNode ? (
          <Inspector
            node={selectedNode}
            onDelete={(nodeId) => {
              if (selectedNode.config.kind === "TRIGGER") {
                void session.bridge.showToast(
                  "Choose another trigger before removing this one.",
                  "WARNING",
                );
                return;
              }
              updateDraft((current) => ({
                ...current,
                nodes: current.nodes.filter((node) => node.id !== nodeId),
                edges: current.edges.filter(
                  (edge) => edge.source !== nodeId && edge.target !== nodeId,
                ),
              }));
              setSelectedNodeId(null);
            }}
            onUpdate={updateNode}
          />
        ) : null}
      </div>
    </main>
  );
}

function NodeLibrary({
  search,
  setSearch,
  hasTrigger,
  onAdd,
}: {
  search: string;
  setSearch: (value: string) => void;
  hasTrigger: boolean;
  onAdd: (kind: WorkflowNodeKind) => void;
}) {
  const filtered = NODE_LIBRARY.filter((item) =>
    `${item.title} ${item.description}`
      .toLowerCase()
      .includes(search.toLowerCase()),
  );
  return (
    <aside className="node-library" aria-label="Workflow step library">
      <div className="panel-heading">
        <strong>Steps</strong>
        <small>Drag or click to add</small>
      </div>
      <label className="search-control compact-search">
        <Search aria-hidden="true" size={15} />
        <span className="sr-only">Search steps</span>
        <input
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search steps"
          value={search}
        />
      </label>
      {(["Start", "Logic", "Actions"] as const).map((group) => {
        const items = filtered.filter((item) => item.group === group);
        if (!items.length) return null;
        return (
          <section className="node-group" key={group}>
            <h2>{group}</h2>
            {items.map((item) => {
              const disabled = item.kind === "TRIGGER" && hasTrigger;
              const Icon = item.icon;
              return (
                <button
                  className="library-item"
                  disabled={disabled}
                  draggable={!disabled}
                  key={item.kind}
                  onClick={() => onAdd(item.kind)}
                  onDragStart={(event) => {
                    event.dataTransfer.setData(
                      "application/crewfsm-workflow-node",
                      item.kind,
                    );
                    event.dataTransfer.effectAllowed = "move";
                  }}
                  type="button"
                >
                  <span>
                    <Icon size={17} />
                  </span>
                  <span>
                    <strong>{item.title}</strong>
                    <small>
                      {disabled ? "Trigger already added" : item.description}
                    </small>
                  </span>
                </button>
              );
            })}
          </section>
        );
      })}
    </aside>
  );
}

function WorkflowCanvas({
  nodes,
  edges,
  onNodesChange,
  onEdgesChange,
  onConnect,
  onSelectNode,
  onAdd,
}: {
  nodes: FlowNode[];
  edges: FlowEdge[];
  onNodesChange: (changes: NodeChange<FlowNode>[]) => void;
  onEdgesChange: (changes: EdgeChange<FlowEdge>[]) => void;
  onConnect: (connection: Connection) => void;
  onSelectNode: (nodeId: string | null) => void;
  onAdd: (kind: WorkflowNodeKind, position: { x: number; y: number }) => void;
}) {
  const [instance, setInstance] = useState<
    | Parameters<
        NonNullable<
          React.ComponentProps<typeof ReactFlow<FlowNode, FlowEdge>>["onInit"]
        >
      >[0]
    | null
  >(null);
  const handleSelectionChange = useCallback(
    ({ nodes: selectedNodes }: { nodes: FlowNode[] }) =>
      onSelectNode(selectedNodes[0]?.id ?? null),
    [onSelectNode],
  );
  return (
    <section className="workflow-canvas" aria-label="Workflow canvas">
      <ReactFlow<FlowNode, FlowEdge>
        defaultViewport={{ x: 24, y: 24, zoom: 1 }}
        deleteKeyCode={["Backspace", "Delete"]}
        edges={edges}
        maxZoom={1.7}
        minZoom={0.35}
        nodeTypes={{ workflowStep: WorkflowStepNode }}
        nodes={nodes}
        onConnect={onConnect}
        onEdgesChange={onEdgesChange}
        onInit={setInstance}
        onNodeClick={(_, node) => onSelectNode(node.id)}
        onNodesChange={onNodesChange}
        onPaneClick={() => onSelectNode(null)}
        onSelectionChange={handleSelectionChange}
        onDragOver={(event) => {
          event.preventDefault();
          event.dataTransfer.dropEffect = "move";
        }}
        onDrop={(event: DragEvent) => {
          event.preventDefault();
          const kind = event.dataTransfer.getData(
            "application/crewfsm-workflow-node",
          ) as WorkflowNodeKind;
          if (!kind || !instance) return;
          onAdd(
            kind,
            instance.screenToFlowPosition({
              x: event.clientX,
              y: event.clientY,
            }),
          );
        }}
        proOptions={{ hideAttribution: true }}
      >
        <Background
          color="var(--app-canvas-dot)"
          gap={18}
          size={1.2}
          variant={BackgroundVariant.Dots}
        />
        <Controls position="bottom-left" showInteractive={false} />
        <MiniMap
          maskColor="color-mix(in srgb, var(--app-background) 82%, transparent)"
          nodeColor="var(--app-primary)"
          pannable
          position="bottom-right"
          zoomable
        />
      </ReactFlow>
    </section>
  );
}

function WorkflowStepNode({ data, selected }: NodeProps<FlowNode>) {
  const node = data.workflowNode;
  const Icon = NODE_ICONS[node.config.kind];
  return (
    <article
      className={`flow-node flow-node-${node.config.kind.toLowerCase()}${selected ? " is-selected" : ""}`}
    >
      {node.config.kind !== "TRIGGER" && (
        <Handle
          className="flow-handle"
          position={Position.Left}
          type="target"
        />
      )}
      <span className="flow-node-icon">
        <Icon size={17} />
      </span>
      <span className="flow-node-copy">
        <small>{titleCase(node.config.kind)}</small>
        <strong>{node.label}</strong>
        <span>{nodeSummary(node)}</span>
      </span>
      {node.config.kind === "CONDITION" ? (
        <>
          <Handle
            className="flow-handle flow-handle-true"
            id="true"
            position={Position.Right}
            style={{ top: "38%" }}
            type="source"
          />
          <Handle
            className="flow-handle flow-handle-false"
            id="false"
            position={Position.Right}
            style={{ top: "70%" }}
            type="source"
          />
          <span className="branch-label branch-true">Yes</span>
          <span className="branch-label branch-false">No</span>
        </>
      ) : (
        <Handle
          className="flow-handle"
          position={Position.Right}
          type="source"
        />
      )}
    </article>
  );
}

function Inspector({
  node,
  onUpdate,
  onDelete,
}: {
  node: WorkflowNode;
  onUpdate: (node: WorkflowNode) => void;
  onDelete: (nodeId: string) => void;
}) {
  const config = node.config;

  return (
    <aside className="inspector" aria-label={`${node.label} settings`}>
      <div className="panel-heading">
        <strong>{node.label}</strong>
        <small>{titleCase(node.config.kind)} settings</small>
      </div>
      {config.kind === "TRIGGER" && (
        <Field label="CrewFSM event">
          <select
            onChange={(event) =>
              onUpdate({
                ...node,
                label:
                  EVENT_LABELS[event.target.value as keyof typeof EVENT_LABELS],
                config: {
                  kind: "TRIGGER",
                  eventType: event.target.value as keyof typeof EVENT_LABELS,
                },
              })
            }
            value={config.eventType}
          >
            {AUTOMATIONS_V1_EVENT_TYPES.map((eventType) => (
              <option key={eventType} value={eventType}>
                {EVENT_LABELS[eventType]}
              </option>
            ))}
          </select>
        </Field>
      )}
      {config.kind === "CONDITION" && (
        <>
          <Field
            hint="Use a dot path from the versioned event payload."
            label="Event field"
          >
            <input
              onChange={(event) =>
                onUpdate({
                  ...node,
                  config: { ...config, field: event.target.value },
                })
              }
              value={config.field}
            />
          </Field>
          <Field label="Operator">
            <select
              onChange={(event) =>
                onUpdate({
                  ...node,
                  config: {
                    ...config,
                    operator: event.target.value as typeof config.operator,
                  },
                })
              }
              value={config.operator}
            >
              <option value="EQUALS">Equals</option>
              <option value="NOT_EQUALS">Does not equal</option>
              <option value="CONTAINS">Contains</option>
              <option value="EXISTS">Exists</option>
            </select>
          </Field>
          {config.operator !== "EXISTS" && (
            <Field label="Value">
              <input
                onChange={(event) =>
                  onUpdate({
                    ...node,
                    config: { ...config, value: event.target.value },
                  })
                }
                value={config.value}
              />
            </Field>
          )}
        </>
      )}
      {config.kind === "DELAY" && (
        <div className="field-grid">
          <Field label="Wait">
            <input
              min={1}
              onChange={(event) =>
                onUpdate({
                  ...node,
                  config: { ...config, amount: Number(event.target.value) },
                })
              }
              type="number"
              value={config.amount}
            />
          </Field>
          <Field label="Unit">
            <select
              onChange={(event) =>
                onUpdate({
                  ...node,
                  config: {
                    ...config,
                    unit: event.target.value as typeof config.unit,
                  },
                })
              }
              value={config.unit}
            >
              <option value="MINUTES">Minutes</option>
              <option value="HOURS">Hours</option>
              <option value="DAYS">Days</option>
            </select>
          </Field>
        </div>
      )}
      {config.kind === "ACTION" && (
        <>
          <Field label="Action">
            <select
              onChange={(event) => {
                const action = event.target.value as ActionKind;
                onUpdate({
                  ...node,
                  label: ACTION_LABELS[action],
                  config: { ...config, action },
                });
              }}
              value={config.action}
            >
              {AUTOMATIONS_V1_ACTION_KINDS.map((action) => (
                <option key={action} value={action}>
                  {ACTION_LABELS[action]}
                </option>
              ))}
            </select>
          </Field>
          <Field
            hint="A role, contact field, stage, status, or HTTPS endpoint."
            label="Target"
          >
            <input
              onChange={(event) =>
                onUpdate({
                  ...node,
                  config: { ...config, target: event.target.value },
                })
              }
              value={config.target}
            />
          </Field>
          <Field label="Message or value">
            <textarea
              onChange={(event) =>
                onUpdate({
                  ...node,
                  config: { ...config, message: event.target.value },
                })
              }
              rows={4}
              value={config.message}
            />
          </Field>
        </>
      )}
      <button
        className="button button-danger inspector-delete"
        disabled={node.config.kind === "TRIGGER"}
        onClick={() => onDelete(node.id)}
        type="button"
      >
        <Trash2 size={15} /> Remove step
      </button>
    </aside>
  );
}

function PageHeader({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return (
    <header className="page-header">
      <div>
        <h1>{title}</h1>
        {description && <p>{description}</p>}
      </div>
      {action}
    </header>
  );
}

function SectionHeading({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return (
    <header className="section-heading">
      <div>
        <h2>{title}</h2>
        {description && <p>{description}</p>}
      </div>
      {action}
    </header>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <article className="metric-card">
      <span>{label}</span>
      <strong>{value}</strong>
    </article>
  );
}

function StatusBadge({ status }: { status: WorkflowStatus }) {
  return (
    <span className={`status-badge status-${status.toLowerCase()}`}>
      {titleCase(status)}
    </span>
  );
}

function StatusDot({ status }: { status: WorkflowStatus }) {
  return (
    <span
      aria-label={titleCase(status)}
      className={`status-dot status-${status.toLowerCase()}`}
    />
  );
}

function RunStatus({ status }: { status: WorkflowExecution["status"] }) {
  return (
    <span
      aria-label={titleCase(status)}
      className={`run-status run-${status.toLowerCase()}`}
    >
      {status === "SUCCEEDED" ? (
        <Check size={14} />
      ) : status === "RUNNING" ? (
        <RefreshCw className="spin" size={14} />
      ) : status === "WAITING" ? (
        <Clock3 size={14} />
      ) : (
        <CircleAlert size={14} />
      )}
    </span>
  );
}

function TextButton({
  children,
  onClick,
}: {
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button className="text-button" onClick={onClick} type="button">
      {children}
      <ChevronRight size={15} />
    </button>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="field">
      <span>{label}</span>
      {children}
      {hint && <small>{hint}</small>}
    </label>
  );
}

function InlineEmpty({ label }: { label: string }) {
  return <div className="inline-empty">{label}</div>;
}

function EmptyState({
  title,
  description,
  icon: Icon,
  actionLabel,
  onAction,
}: {
  title: string;
  description: string;
  icon: LucideIcon;
  actionLabel?: string;
  onAction?: () => void;
}) {
  return (
    <section className="empty-state">
      <span>
        <Icon size={22} />
      </span>
      <h2>{title}</h2>
      <p>{description}</p>
      {actionLabel && onAction && (
        <button
          className="button button-primary"
          onClick={onAction}
          type="button"
        >
          {actionLabel}
        </button>
      )}
    </section>
  );
}

function AppLoading() {
  return (
    <main aria-busy="true" className="center-state" role="status">
      <span className="loader" />
      <h1>Connecting Automations</h1>
      <p>Establishing a secure CrewFSM app session.</p>
    </main>
  );
}

function ConnectionError({ message }: { message: string }) {
  return (
    <main className="center-state" role="alert">
      <span className="error-symbol">!</span>
      <h1>Automations is unavailable</h1>
      <p>{message}</p>
    </main>
  );
}

function MissingWorkflow({ onBack }: { onBack: () => void }) {
  return (
    <main className="center-state" role="alert">
      <span className="error-symbol">!</span>
      <h1>Workflow not found</h1>
      <p>This draft may have been removed from this app installation.</p>
      <button className="button button-primary" onClick={onBack} type="button">
        Back to workflows
      </button>
    </main>
  );
}

function toFlowNodes(
  nodes: WorkflowNode[],
  selectedNodeId?: string | null,
): FlowNode[] {
  return nodes.map((node) => ({
    id: node.id,
    type: "workflowStep",
    position: node.position,
    initialHeight: 70,
    initialWidth: 240,
    selected: node.id === selectedNodeId,
    data: { workflowNode: node },
  }));
}

export function toFlowEdges(edges: WorkflowEdge[]): FlowEdge[] {
  return edges.map((edge) => ({
    id: edge.id,
    source: edge.source,
    target: edge.target,
    sourceHandle: edge.branch?.toLowerCase(),
    data: { branch: edge.branch },
    label: edge.branch ? titleCase(edge.branch) : undefined,
    markerEnd: { type: MarkerType.ArrowClosed, color: "var(--app-edge)" },
    style: { stroke: "var(--app-edge)", strokeWidth: 1.6 },
    labelStyle: { fill: "var(--app-muted)", fontSize: 11, fontWeight: 600 },
  }));
}

function fromFlowEdges(edges: FlowEdge[]): WorkflowEdge[] {
  return edges.map((edge) => ({
    id: edge.id,
    source: edge.source,
    target: edge.target,
    branch: edge.data?.branch,
  }));
}

function triggerLabel(workflow: WorkflowDefinition): string {
  const trigger = workflow.nodes.find((node) => node.config.kind === "TRIGGER");
  return trigger?.config.kind === "TRIGGER"
    ? EVENT_LABELS[trigger.config.eventType]
    : "Trigger missing";
}

function nodeSummary(node: WorkflowNode): string {
  switch (node.config.kind) {
    case "TRIGGER":
      return node.config.eventType;
    case "CONDITION":
      return `${node.config.field} ${titleCase(node.config.operator)}`;
    case "DELAY":
      return `${node.config.amount} ${node.config.unit.toLowerCase()}`;
    case "ACTION":
      return node.config.target;
  }
}

function duplicateWorkflow(workflow: WorkflowDefinition): WorkflowDefinition {
  const now = new Date().toISOString();
  return {
    ...clone(workflow),
    id: createId("workflow"),
    name: `${workflow.name} copy`,
    status: "DRAFT",
    version: 0,
    createdAt: now,
    updatedAt: now,
    publishedVersions: [],
  };
}

function applyTheme(theme: AutomationsAppSession["context"]["themeContract"]) {
  const properties: Record<string, string> = {
    "--app-background": theme.colors.background,
    "--app-surface": theme.colors.surface,
    "--app-foreground": theme.colors.foreground,
    "--app-muted": theme.colors.mutedForeground,
    "--app-primary": theme.colors.primary,
    "--app-primary-foreground": theme.colors.primaryForeground,
    "--app-accent": theme.colors.accent,
    "--app-accent-foreground": theme.colors.accentForeground,
    "--app-border": theme.colors.border,
    "--app-input": theme.colors.input,
    "--app-ring": theme.colors.ring,
    "--app-success": theme.colors.success,
    "--app-warning": theme.colors.warning,
    "--app-danger": theme.colors.danger,
    "--app-info": theme.colors.info,
    "--app-radius-sm": theme.shape.radiusSmall,
    "--app-radius-md": theme.shape.radiusMedium,
    "--app-radius-lg": theme.shape.radiusLarge,
    "--app-font": theme.typography.fontFamily,
    "--app-font-weight-regular": String(theme.typography.fontWeightRegular),
    "--app-font-weight-medium": String(theme.typography.fontWeightMedium),
    "--app-font-weight-semibold": String(theme.typography.fontWeightSemibold),
    "--app-font-weight-bold": String(theme.typography.fontWeightBold),
  };
  for (const [property, value] of Object.entries(properties))
    document.documentElement.style.setProperty(property, value);
}

function titleCase(value: string): string {
  return value
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatRelative(value: string): string {
  const seconds = Math.max(
    0,
    Math.floor((Date.now() - new Date(value).getTime()) / 1000),
  );
  if (seconds < 60) return "Just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value));
}

function formatTime(value: string): string {
  return new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  }).format(new Date(value));
}

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function errorMessage(error: unknown): string {
  return error instanceof Error
    ? error.message
    : "CrewFSM could not complete this request.";
}
